import { isDefined } from "@clipboard-health/util-ts";
import { Box, Paper, Stack } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { type WorkplaceCommentModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { Text, type UseModalState, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useGetComment } from "@src/appV2/redesign/Workplace/Review/api/useGetComment";
import {
  ReviewSource,
  useWorkplaceComments,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { useParams } from "react-router-dom";

import { CommentReactionSource } from "../types";
import { isViewerAuthor } from "../utils";
import { WorkplaceReviewCommentCard } from "./Card";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";
import { ModalCommentEngagementActions } from "./ModalCommentEngagementActions";
import { WorkplaceCommentPostReplyFooter } from "./PostReplyFooter";
import { ReviewContextPills } from "./ReviewContextPills";
import { ScrollableCommentText } from "./ScrollableCommentText";
import { useUpdateCachedComment } from "./useUpdateCachedComment";
import { VideoCommentPlayer } from "./VideoCommentPlayer";

interface WorkplaceCommentModalProps {
  modalState: UseModalState;
  workplaceId: string;
}

export function WorkplaceCommentModal(props: WorkplaceCommentModalProps) {
  const { modalState, workplaceId } = props;

  const { commentId } = useParams<WorkplaceCommentModalRoutePathParams>();
  const worker = useDefinedWorker();

  const { useDelayedReviewUI } = useCbhFlag(CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW],
  });

  // TODO: This modal needs to be split into a container component that handles loading states
  // and a presentational component that receives the destructured comment data
  const { data: commentData, isSuccess: isCommentSuccess } = useGetComment({
    workplaceId,
    commentId,
  });

  const commentsParams = {
    workplaceId,
    filter: {
      parentCommentId: commentId,
    },
  };

  const source = commentData?.data.attributes.source;
  const externalDetails = commentData?.data.attributes.externalDetails;
  const isExternalReview = isDefined(source) && source !== ReviewSource.CBH;

  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(commentData?.data.attributes.workerInfo.id), {
      enabled: isDefined(commentData?.data.attributes.workerInfo.id) && !isExternalReview,
    });

  const profilePictureUrl = isExternalReview
    ? externalDetails?.reviewerPhotoUrl
    : profilePicture?.distribution_url;

  let displayDate = new Date();
  if (isExternalReview && isDefined(externalDetails?.publishedAt)) {
    displayDate = parseISO(externalDetails.publishedAt);
  } else if (isDefined(commentData)) {
    displayDate = parseISO(commentData.data.attributes.createdAt);
  }

  const { data: replies, isSuccess: isRepliesSuccess } = useWorkplaceComments(commentsParams);

  const sortedReplies =
    replies?.toSorted((a, b) => {
      return (
        new Date(b.attributes.createdAt).getTime() - new Date(a.attributes.createdAt).getTime()
      );
    }) ?? [];

  const { updateComment, deleteComment } = useUpdateCachedComment({
    workplaceId,
    commentsParams,
  });

  const { reviewsViewContext } = useWorkplaceModalsDataContext();
  const removeModalState = useModalState();
  const viewerIsAuthor =
    isDefined(commentData) &&
    isViewerAuthor({
      workerInfo: commentData.data.attributes.workerInfo,
      viewerUserId: worker.userId,
    });

  const engagementProps = isCommentSuccess
    ? {
        workerReaction: commentData.data.attributes.workerReaction ?? undefined,
        totalLikes: commentData.data.attributes.aggregates.totalLikes,
        totalDislikes: commentData.data.attributes.aggregates.totalDislikes,
        totalReplies: commentData.data.attributes.aggregates.totalReplies,
        workplaceId,
        commentId,
        reactionSource: CommentReactionSource.COMMENT_DETAIL_MODAL,
        viewContext: reviewsViewContext,
        viewerIsAuthor,
        isExternalReview,
        onReactionChanged: (newComment: WorkplaceCommentType) => {
          updateComment(newComment);
        },
        onRemove: () => {
          removeModalState.openModal();
        },
      }
    : undefined;

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <PageHeaderTopRow />

      <Stack sx={{ overflow: "hidden", height: "100%" }}>
        <Paper variant="tertiary" sx={{ px: 7, paddingBottom: 8 }}>
          {isCommentSuccess &&
            (useDelayedReviewUI ? (
              <Stack spacing={4}>
                <WorkplaceReviewCommentAuthorInfo
                  useDelayedReviewLayout
                  workerName={commentData.data.attributes.workerInfo.name}
                  profilePictureUrl={profilePictureUrl}
                  isLoadingProfilePicture={isExternalReview ? false : isProfilePictureLoading}
                  createdAt={displayDate}
                  shiftWorkerQualification={commentData.data.attributes.shiftWorkerQualification}
                  reviewUrl={externalDetails?.reviewUrl}
                />

                <ReviewContextPills
                  shiftPeriod={commentData.data.attributes.shiftPeriod}
                  patientsPerNurse={commentData.data.attributes.patientsPerNurse}
                  reviewTag={commentData.data.attributes.reviewTag}
                  rating={commentData.data.attributes.rating}
                  source={source}
                />

                {commentData.data.attributes.type === "VIDEO" &&
                isDefined(commentData.data.attributes.media?.[0]?.url) ? (
                  <VideoCommentPlayer
                    url={commentData.data.attributes.media[0].url}
                    workplaceId={workplaceId}
                    commentId={commentId}
                  />
                ) : (
                  <ScrollableCommentText text={commentData.data.attributes.text} />
                )}

                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                  {engagementProps && <ModalCommentEngagementActions {...engagementProps} />}
                </Stack>
              </Stack>
            ) : (
              <>
                {commentData.data.attributes.type === "VIDEO" &&
                isDefined(commentData.data.attributes.media?.[0]?.url) ? (
                  <VideoCommentPlayer
                    url={commentData.data.attributes.media[0].url}
                    workplaceId={workplaceId}
                    commentId={commentId}
                  />
                ) : (
                  <Box sx={{ marginBottom: 8 }}>
                    <ScrollableCommentText text={commentData.data.attributes.text} />
                  </Box>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                  <WorkplaceReviewCommentAuthorInfo
                    workerName={commentData.data.attributes.workerInfo.name}
                    profilePictureUrl={profilePictureUrl}
                    isLoadingProfilePicture={isExternalReview ? false : isProfilePictureLoading}
                    createdAt={displayDate}
                    reviewUrl={externalDetails?.reviewUrl}
                  />

                  {engagementProps && <ModalCommentEngagementActions {...engagementProps} />}
                </Stack>
              </>
            ))}
        </Paper>

        <Paper variant="secondary" sx={{ px: 5, py: 7, flexGrow: 1, overflow: "auto" }}>
          {isRepliesSuccess && (
            <Stack spacing={5}>
              {sortedReplies.length === 0 && (
                <Text variant="h6" sx={{ textAlign: "center" }}>
                  No replies yet. Be the first to reply!
                </Text>
              )}

              {sortedReplies.map((reply) => (
                <WorkplaceReviewCommentCard
                  key={reply.id}
                  workplaceId={workplaceId}
                  comment={reply}
                  reactionSource={CommentReactionSource.COMMENT_DETAIL_MODAL}
                  viewContext={reviewsViewContext}
                  showRepliesCount={false}
                  viewerIsAuthor={isViewerAuthor({
                    workerInfo: reply.attributes.workerInfo,
                    viewerUserId: worker.userId,
                  })}
                  onLikeChanged={(newComment) => {
                    updateComment(newComment);
                  }}
                  onAnonymized={(newComment) => {
                    updateComment(newComment);
                  }}
                  onRemoved={(commentId) => {
                    deleteComment(commentId);
                  }}
                />
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>

      <WorkplaceCommentPostReplyFooter workplaceId={workplaceId} parentId={String(commentId)} />
      {viewerIsAuthor && !isExternalReview && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={commentData.data.id}
          workplaceId={workplaceId}
          onRemoved={(commentId) => {
            deleteComment(commentId);
            modalState.closeModal();
          }}
        />
      )}
    </FullScreenDialog>
  );
}
